import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Megaphone, TrendingUp, Bot, Briefcase, LayoutDashboard, Phone, Settings2, Download, Loader2, FileBarChart } from 'lucide-react';
import DashboardFilters, { DEFAULT_FILTERS, filtersToParams } from '@/components/dashboard/DashboardFilters';
import DashboardSettings from '@/components/dashboard/DashboardSettings';
import useChartExport from '@/hooks/useChartExport';
import { formatPeriodLabel } from '@/utils/pdfExecutive';
import api from '@/lib/api';
import { verticalFromUser, VERTICAL_BADGE_LABEL, VendorDashboard } from '@/dashboards/registry';
import OnboardingChecklist from '@/components/OnboardingChecklist';
import WelcomeOnboardingModal from '@/components/onboarding/WelcomeOnboardingModal';
import { DASHBOARD_SECTIONS, isTabHidden, isChartHidden, getExecutiveFixedChart } from '@/dashboards/dashboardSections';
import MarketingTab from '@/dashboards/tabs/MarketingTab';
import VentasTab from '@/dashboards/tabs/VentasTab';
import IATab from '@/dashboards/tabs/IATab';
import DireccionTab from '@/dashboards/tabs/DireccionTab';
import CallForceTab from '@/dashboards/tabs/CallForceTab';

// Clases LITERALES a propósito (no se construyen con template strings): el
// JIT de Tailwind escanea el código fuente en busca de nombres de clase
// completos -- una clase armada como `bg-${color}-400/15` en runtime nunca
// se compila y el color simplemente no aparece.
const TAB_META = {
  marketing: { icon: Megaphone, Component: MarketingTab, activeClass: 'data-[state=active]:bg-cyan-400/15 data-[state=active]:text-cyan-400' },
  ventas: { icon: TrendingUp, Component: VentasTab, activeClass: 'data-[state=active]:bg-emerald-400/15 data-[state=active]:text-emerald-400' },
  ia: { icon: Bot, Component: IATab, activeClass: 'data-[state=active]:bg-violet-400/15 data-[state=active]:text-violet-400' },
  direccion: { icon: Briefcase, Component: DireccionTab, activeClass: 'data-[state=active]:bg-amber-400/15 data-[state=active]:text-amber-400' },
  callforce: { icon: Phone, Component: CallForceTab, activeClass: 'data-[state=active]:bg-sky-400/15 data-[state=active]:text-sky-400' },
};

/**
 * Dashboard principal — 4-5 áreas tropicalizadas por licencia, personalizables
 * por usuario (dashboard-personalizable): cada quien puede ocultar cuadrantes
 * completos, gráficos individuales dentro de un cuadrante, y cambiar el tipo
 * de visualización de algunos gráficos. Persistido en users.dashboard_prefs
 * (lista NEGRA -- CLAUDE.md §3 #9, un cuadrante/gráfico nuevo sigue visible
 * por defecto).
 *
 * Para vendedores (role=vendor): se muestra `VendorDashboard` simplificado
 * en lugar de las tabs (no tienen permiso de vista global; tampoco se
 * personaliza -- alcance de esta feature es solo la vista de admin/manager).
 */
export default function DashboardPage() {
  const { user } = useAuth();
  const vertical = verticalFromUser(user);
  const isVendor = user?.role === 'vendor';
  const caps = user?.license_profile?.capabilities || {};
  const showCallForce = caps.callforce !== false;
  const prefs = user?.dashboard_prefs;

  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { exportDashboard, exporting: exportingDashboard } = useChartExport();
  const { exportExecutive, exporting: exportingExecutive } = useChartExport();

  // Gate por licencia/rol PRIMERO (igual que antes), preferencia de usuario ENCIMA.
  const gatedSections = useMemo(
    () => DASHBOARD_SECTIONS.filter((s) => s.key !== 'callforce' || showCallForce),
    [showCallForce],
  );
  const visibleSections = useMemo(
    () => gatedSections.filter((s) => !isTabHidden(prefs, s.key)),
    [gatedSections, prefs],
  );

  const [tabValue, setTabValue] = useState(visibleSections[0]?.key || 'marketing');
  useEffect(() => {
    if (visibleSections.length && !visibleSections.some((s) => s.key === tabValue)) {
      setTabValue(visibleSections[0].key);
    }
  }, [visibleSections, tabValue]);

  const handleDownloadDashboard = async () => {
    const prevTab = tabValue;
    const sectionsForPdf = visibleSections.map((s) => ({
      key: s.key,
      label: s.label,
      charts: (s.charts || []).filter((c) => !isChartHidden(prefs, s.key, c.id)),
    }));

    await exportDashboard({
      company: { name: user?.company_name || user?.company?.name, logo: user?.company_logo },
      userName: user?.name,
      visibleSections: sectionsForPdf,
      switchTab: (key) => { setTabValue(key); return Promise.resolve(); },
      waitMs: 1800,
      periodLabel: formatPeriodLabel(filters.date_from, filters.date_to),
    });
    setTabValue(prevTab);
  };

  const handleDownloadExecutive = async () => {
    const prevTab = tabValue;
    const params = { ...filtersToParams(filters), vertical };
    const kpis = [];

    // El Ejecutivo IGNORA la personalización a propósito (gráficos fijos,
    // siempre los mismos) -- por eso los KPIs se gatean por `gatedSections`
    // (licencia/rol), NO por `visibleSections` (que además resta lo que el
    // usuario ocultó). KPIs SOLO de endpoints reales -- si un valor no viene
    // en la respuesta, se omite (nunca se rellena con dato inventado).
    try {
      if (gatedSections.some((s) => ['marketing', 'ventas', 'direccion'].includes(s.key))) {
        const r = await api.get('/dashboard/v2/kpis', { params }).catch(() => null);
        const core = r?.data?.core;
        if (core) {
          if (core.total_leads != null) kpis.push({ label: 'Leads totales', value: String(core.total_leads) });
          if (core.conversion_rate != null) kpis.push({ label: 'Conversión global', value: `${core.conversion_rate}%` });
          if (core.total_pipeline_value != null) kpis.push({ label: 'Pipeline', value: `$${(core.total_pipeline_value / 1000).toFixed(1)}k`, sub: 'MXN' });
          if (core.won_leads != null) kpis.push({ label: 'Ganados', value: String(core.won_leads) });
        }
      }
      if (gatedSections.some((s) => s.key === 'marketing')) {
        const r = await api.get('/dashboard/v2/charts', { params }).catch(() => null);
        const sources = r?.data?.leads_by_source;
        // != null (no truthy-check de conteo): 0 fuentes es una respuesta real
        // del endpoint, igual que "0 leads" en los otros KPIs -- se muestra,
        // no se omite. Omitir es solo para cuando el endpoint no respondió.
        if (sources != null) {
          kpis.push({ label: 'Fuentes activas', value: String(Object.keys(sources).length) });
        }
      }
      if (gatedSections.some((s) => s.key === 'ia')) {
        const r = await api.get('/ai-agent/stats', { params }).catch(() => null);
        if (r?.data?.conversion_rate != null) {
          kpis.push({ label: 'Tasa de respuesta IA', value: `${r.data.conversion_rate}%` });
        }
      }
    } catch {
      // los KPIs son best-effort -- el reporte se genera igual con los que si llegaron
    }

    // Gráficos FIJOS -- uno representativo por tab disponible por licencia
    // (gatedSections), nunca filtrado por dashboard_prefs del usuario.
    const fixedCharts = gatedSections
      .map((section) => ({ section, chart: getExecutiveFixedChart(section.key) }))
      .filter((x) => x.chart);

    await exportExecutive({
      company: { name: user?.company_name || user?.company?.name, logo: user?.company_logo },
      userName: user?.name,
      fixedCharts,
      kpis,
      switchTab: (key) => { setTabValue(key); return Promise.resolve(); },
      waitMs: 1800,
      periodLabel: formatPeriodLabel(filters.date_from, filters.date_to),
    });
    setTabValue(prevTab);
  };

  if (isVendor) {
    return (
      <div data-testid="dashboard-page">
        <VendorDashboard />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <OnboardingChecklist />
      <WelcomeOnboardingModal />
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <LayoutDashboard className="w-5 h-5 text-primary" />
        <h2 className="font-heading text-2xl font-bold text-white">Dashboards</h2>
        <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground bg-white/5 px-2 py-0.5 rounded">
          {VERTICAL_BADGE_LABEL[vertical] || VERTICAL_BADGE_LABEL.generic}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={handleDownloadDashboard}
            disabled={exportingDashboard || !visibleSections.length}
            data-testid="dashboard-download-btn"
            className="flex items-center gap-1.5 h-8 px-3 rounded-md bg-black/20 border border-white/10 text-xs text-muted-foreground hover:text-white hover:bg-black/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {exportingDashboard ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Descargar Dashboard
          </button>
          <button
            type="button"
            onClick={handleDownloadExecutive}
            disabled={exportingExecutive || !visibleSections.length}
            data-testid="dashboard-executive-btn"
            title="Reporte vectorial (jsPDF + svg2pdf.js): portada, resumen de KPIs y gráficos nítidos por sección"
            className="flex items-center gap-1.5 h-8 px-3 rounded-md bg-black/20 border border-white/10 text-xs text-muted-foreground hover:text-white hover:bg-black/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {exportingExecutive ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileBarChart className="w-3.5 h-3.5" />}
            Descargar Reporte Ejecutivo
          </button>
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            data-testid="dashboard-settings-btn"
            className="flex items-center gap-1.5 h-8 px-3 rounded-md bg-black/20 border border-white/10 text-xs text-muted-foreground hover:text-white hover:bg-black/30 transition-colors"
          >
            <Settings2 className="w-3.5 h-3.5" />
            Personalizar
          </button>
        </div>
      </div>

      <DashboardFilters value={filters} onChange={setFilters} />

      {visibleSections.length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-card/40 p-8 text-center text-sm text-muted-foreground" data-testid="dashboard-all-hidden">
          Ocultaste todos los cuadrantes. Usa "Personalizar" para mostrar alguno de nuevo.
        </div>
      ) : (
        <Tabs value={tabValue} onValueChange={setTabValue} className="w-full">
          <TabsList className="bg-[#1F2833]/80 border border-white/5 mb-6 flex w-full overflow-x-auto no-scrollbar justify-start sm:inline-flex sm:w-auto" data-testid="dashboard-tabs">
            {visibleSections.map((s) => {
              const Icon = TAB_META[s.key]?.icon || LayoutDashboard;
              return (
                <TabsTrigger
                  key={s.key}
                  value={s.key}
                  className={`${TAB_META[s.key]?.activeClass || ''} text-xs gap-1.5`}
                  data-testid={`tab-${s.key}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {s.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* gatedSections (no visibleSections): el Reporte Ejecutivo cambia a
              TODAS las tabs disponibles por licencia -- incluidas las que el
              usuario ocultó vía Personalizar -- para capturar su gráfico fijo.
              Sin esto, una tab oculta no tendría TabsContent que montar y su
              card saldría "no disponible" en el Ejecutivo (§3 #9/#14a: nunca
              perder contenido en silencio). El TabsList de arriba SÍ sigue
              filtrado por visibleSections -- el usuario solo ve como botón lo
              que eligió mostrar. */}
          {gatedSections.map((s) => {
            const Component = TAB_META[s.key]?.Component;
            if (!Component) return null;
            return (
              <TabsContent key={s.key} value={s.key}>
                <Component filters={filters} vertical={vertical} />
              </TabsContent>
            );
          })}
        </Tabs>
      )}

      <DashboardSettings open={settingsOpen} onOpenChange={setSettingsOpen} sections={gatedSections} />
    </div>
  );
}
